import React, { useRef, useState, useEffect } from 'react';
import Play from '@/assets/play.svg';
import { ImageModel } from '@/types';

type VideoBlobStorageElementModel = {
  videoUrl: string;
  coverImage?: ImageModel;
  summary?: string;
  title?: string;
  directory?: {
    name: string;
    date: string;
  };
} & React.HTMLAttributes<HTMLDivElement>;

const VideoBlobStorageElement = ({
  videoUrl,
  coverImage,
  summary,
  title,
  directory,
  ...props
}: VideoBlobStorageElementModel) => {
  const [started, setStarted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video && video.readyState >= 1) {
      const ratio = video.videoWidth / video.videoHeight;
      setAspectRatio(ratio);
    }
  }, []);

  if (!videoUrl) {
    return null;
  }

  const onStartClick = () => {
    setStarted(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    const ratio = video.videoWidth / video.videoHeight;
    setAspectRatio(ratio);
  };

  return (
    <div {...props}>
      <div
        className="relative w-full p-3 print:hidden"
        style={{
          aspectRatio: aspectRatio || '16 / 9', // fallback until loaded
        }}
      >
        <video
          controlsList="nodownload"
          preload="auto"
          poster={coverImage ? coverImage.imageUrl : undefined}
          className="h-full w-full object-cover"
          controls={started}
          ref={videoRef}
          src={videoUrl}
          onLoadedMetadata={handleLoadedMetadata}
        />
        {!started && (
          <div
            className="group absolute top-0 left-0 flex h-full w-full cursor-pointer items-center justify-center"
            onClick={onStartClick}
          >
            <button className="flex h-12 w-22 items-center justify-center rounded-xl border border-white bg-black/50 transition-colors duration-300 group-hover:bg-black/20">
              <Play className="h-7 w-7 fill-white" />
            </button>
          </div>
        )}
      </div>
      {directory && (
        <div className="mb-4 border-b border-b-[#ccc] text-xs">
          <span>{directory.name}</span>
          {directory.name && directory.date && <span> | </span>}
          <span>{directory.date}</span>
        </div>
      )}
      {(summary || title) && (
        <div className="border-b border-b-[#ccc] py-4">
          {title && <div className="text-base font-bold">{title}</div>}
          {summary && <div className="mt-3 text-sm">{summary}</div>}
        </div>
      )}
    </div>
  );
};

export default VideoBlobStorageElement;
